import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    HostBinding,
    Inject,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { RacingOptionMarketTypes } from '@frontend/sports/racing/feature/models';
import { Store, createSelector } from '@ngrx/store';
import { Observable, Subscription, map, withLatestFrom } from 'rxjs';

import { StakeValidationResult } from '../../../../../lib/betslip-base/stake-validation/stake-validation.models';
import { NumpadSource, StakeChangedEvent } from '../../../../../lib/numpad/model';
import { NumpadService } from '../../../../../lib/numpad/numpad.service';
import { IBetslipMessages } from '../../../../base/models/betslip-messages';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipType } from '../../../../core/betslip-type';
import { betslipPickStateSelector } from '../../../picks/selectors';
import { isPickEachWayCapable } from '../../../picks/services/betslip-pick-helpers';
import { isFreebetToken } from '../../../reward-tokens/services/reward-tokens.utils';
import { selectSummaryRewardToken } from '../../../summary/components/digital/summary.component';
import { selectStakeValidationState } from '../../../summary/selectors';
import { BetslipTypesActions } from '../../../types/actions';
import { betslipTypeStateSelector } from '../../../types/selectors';
import { BETSLIP_SUMMARY_NUMPAD } from '../../models';
import { BetslipStakeValidationService } from '../../services/digital/betslip-stake-validation.service';
import StakeConvertService from '../../services/stake-convert.service';

export const selectSummaryFreeBetToken = createSelector(selectSummaryRewardToken, (token) => (isFreebetToken(token) ? token : null));

@Component({
    selector: 'bs-digital-summary-stake',
    templateUrl: './summary-stake.component.html',
    providers: [NumpadService],
    styleUrls: ['./summary-stake.component.scss'],
    // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export default class SummaryStakeComponent implements OnInit {
    @Input() stake: number | null;
    @Output() stakeChange = new EventEmitter<string>();
    @HostBinding('class') className = 'summary-stake';
    @Input() keypadId = BETSLIP_SUMMARY_NUMPAD;

    numpadSource = NumpadSource.BetSlip;
    showEachWayButton = false;
    showEachWayText = false;
    buttonDisable: boolean;
    isEachWaySelected = false;
    messages: IBetslipMessages;
    title: string;
    hasStakeError: Observable<boolean>;
    error: Nullable<StakeValidationResult>;
    errorsSubscription: Subscription;

    readonly freeBetToken$ = this.store.select(selectSummaryFreeBetToken);

    constructor(
        private stakeConvert: StakeConvertService,
        private store: Store<IBetslipRootState>,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) betslipMessageProviderService: IBetslipMessagesProviderService,
        private stakeValidator: BetslipStakeValidationService,
        private changeDetector: ChangeDetectorRef,
        private destroyRef: DestroyRef,
    ) {
        this.messages = betslipMessageProviderService.getMessages();
        this.title = this.messages.common.EachWayAbbr;
    }

    ngOnInit(): void {
        this.store
            .select(betslipTypeStateSelector)
            .pipe(withLatestFrom(this.store.select(betslipPickStateSelector)), takeUntilDestroyed(this.destroyRef))
            .subscribe(([typeState, pickState]) => {
                this.showEachWayButton =
                    [BetslipType.Combo, BetslipType.System].includes(typeState.base.currentSelectedType!) &&
                    Object.values(pickState.pickList).some(isPickEachWayCapable);
                switch (typeState.base.currentSelectedType) {
                    case BetslipType.Single:
                        this.showEachWayText =
                            pickState.pickList.length === 1 && Object.values(typeState.singleBet.picks).some((pick) => pick.isEachWay);
                        break;
                    case BetslipType.Combo:
                        this.showEachWayText = typeState.comboBet.isEachWay;
                        break;
                    case BetslipType.System:
                        this.showEachWayText = typeState.systemBet.isEachWay;
                        break;
                    default:
                        break;
                }

                if (this.showEachWayButton) {
                    this.isEachWaySelected =
                        typeState.base.currentSelectedType! === BetslipType.Combo ? typeState.comboBet.isEachWay : typeState.systemBet.isEachWay;
                }
                this.buttonDisable = pickState.pickList.some((p) => p.market?.marketType?.toString() === RacingOptionMarketTypes.WinEwExtraPlace);

                this.changeDetector.markForCheck();
            });
    }

    setStake(event: StakeChangedEvent) {
        this.stakeChange.emit(event.stake);
    }

    onEachWayClick(event: MouseEvent) {
        event.stopPropagation();
        this.store.dispatch(BetslipTypesActions.setEachWay({ isEachWay: !this.isEachWaySelected }));
    }

    /**
     * The stake component provides the stake, but the betslip validates if it is a valid value and if not then we
     * send the result to the stake component
     */
    hasStakeError$ = this.store.select(selectStakeValidationState).pipe(
        map(({ stakeError, stake }) => {
            return !!stake && !!this.stakeValidator.validateStake(stakeError, stake);
        }),
    );

    get stakeAsStr(): string {
        return this.stakeConvert.convert(this.stake);
    }
}
